import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener,
  Input, OnChanges, Output, SimpleChanges, computed, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  DEFAULT_SAAS_DAY_OPTIONS, DeviceStep1UiRow, DeviceStep2UiRow, FormData, FormDataApprover,
  FormDataDeviceSku, LicextAction, LicextChatMessage, SaasDays, SaasStep1UiRow, SaasStep2UiRow,
} from '../../models/licext.model';
import { LicextApproverPickerComponent } from '../licext-approver-picker/licext-approver-picker.component';

/**
 * Inline License Extension wizard, rendered within an agent message bubble.
 *
 * Mirrors the legacy wg-agent-hub-ui licextagent-chat wizard but is purely a
 * renderer: it reads the `licext_form` payload off the message, lets the user
 * fill/edit the rows, and emits a single `submit` action carrying the composed
 * seed message. The host (agenthome-chat) sends it through the normal chat
 * pipeline and marks the form submitted. All WS/session plumbing lives in the
 * host — this component is self-contained and theme-aware (uses --wg-* vars).
 *
 * Four form shapes (driven by form.flow_type):
 *   device_step1 → rows of {serial} (+ optional invalid/error flag)
 *   device_step2 → frozen serial/account rows with a per-device SKU picker
 *   saas_step1   → rows of {container_key}
 *   saas_step2   → frozen container/account rows with a product picker + days
 */
@Component({
  selector: 'app-licext-renderer',
  standalone: true,
  imports: [CommonModule, FormsModule, LicextApproverPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './licext-renderer.component.html',
  styleUrls: ['./licext-renderer.component.scss'],
})
export class LicextRendererComponent implements OnChanges {
  @Input() msg!: LicextChatMessage;
  @Input() msgIdx = 0;
  /** True when this message is no longer the latest agent message → read-only. */
  @Input() isPast = false;
  /** True while the agent is still processing the current turn. */
  @Input() isProcessing = false;

  @Output() action = new EventEmitter<LicextAction>();

  readonly deviceStep1Rows = signal<DeviceStep1UiRow[]>([]);
  readonly deviceStep2Rows = signal<DeviceStep2UiRow[]>([]);
  readonly saasStep1Rows = signal<SaasStep1UiRow[]>([]);
  readonly saasStep2Rows = signal<SaasStep2UiRow[]>([]);
  readonly saasDayOptions = signal<SaasDays[]>(DEFAULT_SAAS_DAY_OPTIONS);
  readonly reason = signal('');
  readonly accountName = signal('');

  // ── Searchable per-device SKU dropdown (device step 2) ────────────────────
  // Mirrors the legacy portal's filterable p-select: a per-row combobox with a
  // type-to-filter search box, rendered inline (no overlay) so the card's
  // overflow never clips it. Options come from each row's applicable SKUs.
  readonly openSkuRow = signal<number | null>(null);
  readonly skuQuery = signal('');
  /** Index (within filteredSkus(rowIdx)) of the keyboard-highlighted option. */
  readonly skuActiveIdx = signal(0);

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  /** Human label for a SKU option: "<sku> (<term>)" when a term is present. */
  skuLabel(s: FormDataDeviceSku): string {
    return s.term ? `${s.sku} (${s.term})` : (s.sku ?? '');
  }

  /** Trim an API expiry timestamp (e.g. "2026-06-22T11:19:00.0000000+00:00") to its
   *  YYYY-MM-DD date portion for display. */
  formatExpiry(value?: string): string {
    return (value ?? '').slice(0, 10);
  }

  /** Success-card sub-text, phrased to match the actual outcome:
   *  - some items sent for approval → "once approved and processed"
   *  - everything auto-approved     → "once processed"
   *  - mixed                        → "as each item is processed" */
  get completeSubMessage(): string {
    const c = this.msg?.licextFlowComplete;
    const flow = c?.flow_type === 'saas' ? 'SaaS' : 'device';
    const pending = c?.pending_count ?? 0;
    const direct = c?.direct_count ?? 0;
    let tail: string;
    if (pending > 0 && direct > 0) tail = "You'll receive follow-up notifications as each item is processed.";
    else if (pending > 0) tail = "You'll receive follow-up notifications once approved and processed.";
    else tail = "You'll receive follow-up notifications once processed.";
    return `Your ${flow} license extension request has been submitted. ${tail}`;
  }

  /** Label for a step-2 row's currently selected SKU (with its term if known). */
  selectedLabel(row: DeviceStep2UiRow): string {
    if (!row.selectedSku) return '';
    const match = row.skus.find(s => s.sku === row.selectedSku);
    return match ? this.skuLabel(match) : row.selectedSku;
  }

  /** The applicable SKUs for a row, filtered by the search box. */
  filteredSkus(rowIdx: number): FormDataDeviceSku[] {
    const q = this.skuQuery().trim().toLowerCase();
    const all = this.deviceStep2Rows()[rowIdx]?.skus ?? [];
    if (!q) return all;
    return all.filter(s =>
      (s.sku ?? '').toLowerCase().includes(q) || (s.term ?? '').toLowerCase().includes(q));
  }

  toggleSkuPanel(i: number): void {
    if (this.locked) return;
    this.openSkuRow.update(cur => (cur === i ? null : i));
    this.skuQuery.set('');
    // Highlight the row's current SKU if it's in the list, else the first option.
    const opts = this.filteredSkus(i);
    const cur = this.deviceStep2Rows()[i]?.selectedSku ?? '';
    this.skuActiveIdx.set(Math.max(0, opts.findIndex(s => s.sku === cur)));
    // Focus the search box on open so arrow keys/typing work without a second click.
    if (this.openSkuRow() === i) {
      setTimeout(() => {
        this.host.nativeElement.querySelector<HTMLInputElement>('.licext-combo__search')?.focus();
      });
    }
  }

  /** Search-box input: filter and reset the highlight to the top of the results. */
  onSkuSearch(value: string): void {
    this.skuQuery.set(value);
    this.skuActiveIdx.set(0);
  }

  /** Arrow keys move the highlight, Enter selects it, Escape closes the panel. */
  onSkuKeydown(rowIdx: number, event: KeyboardEvent): void {
    const opts = this.filteredSkus(rowIdx);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (opts.length) {
          this.skuActiveIdx.update(n => Math.min(n + 1, opts.length - 1));
          this.scrollActiveIntoView();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (opts.length) {
          this.skuActiveIdx.update(n => Math.max(n - 1, 0));
          this.scrollActiveIntoView();
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const opt = opts[this.skuActiveIdx()];
        if (opt?.sku) this.selectSku(rowIdx, opt.sku);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.openSkuRow.set(null);
        this.skuQuery.set('');
        break;
    }
  }

  /** Keep the highlighted option visible within the scrollable list. */
  private scrollActiveIntoView(): void {
    // Defer so the --active class binding has flushed to the DOM first.
    setTimeout(() => {
      this.host.nativeElement
        .querySelector('.licext-combo__opt--active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  /** Collapse the open SKU dropdown when the user clicks anywhere outside its
   *  trigger or panel (the panel is a sibling of the combo, so we match both). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    // Clicks inside any combo (device SKU picker or SaaS products dropdown) keep
    // it open — including ticking a product or a day pill in the panel.
    if (target?.closest('.licext-combo, .licext-combo__panel')) return;
    if (this.openSkuRow() !== null) {
      this.openSkuRow.set(null);
      this.skuQuery.set('');
    }
    if (this.saasStep2Rows().some(r => r.panelOpen)) {
      this.saasStep2Rows.update(rows => rows.map(r => (r.panelOpen ? { ...r, panelOpen: false } : r)));
    }
  }

  selectSku(i: number, sku: string): void {
    this.deviceStep2Rows.update(rows => rows.map((r, idx) => idx === i ? { ...r, selectedSku: sku } : r));
    this.openSkuRow.set(null);
    this.skuQuery.set('');
  }

  clearSku(i: number): void {
    if (this.locked) return;
    this.deviceStep2Rows.update(rows => rows.map((r, idx) => idx === i ? { ...r, selectedSku: null } : r));
  }

  /** Reason word limit — mirrors the legacy portal and the backend's
   *  submit_licext_request (max 500 words). */
  readonly REASON_MAX_WORDS = 500;
  readonly wordCount = computed(() => {
    const t = this.reason().trim();
    return t ? t.split(/\s+/).length : 0;
  });

  /** Live-cap the reason at 500 words (and 50 chars/word), like the old portal,
   *  so the user can't paste past the limit. */
  onReasonInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    let val = el.value;
    const words = val.trim() ? val.trim().split(/\s+/) : [];
    const capped = words.map(w => (w.length > 50 ? w.slice(0, 50) : w));
    if (capped.length > this.REASON_MAX_WORDS || capped.some((w, i) => w !== words[i])) {
      val = capped.slice(0, this.REASON_MAX_WORDS).join(' ');
      el.value = val;
    }
    this.reason.set(val);
  }

  /** Live update of the Account Name field. */
  onAccountNameInput(event: Event): void {
    this.accountName.set((event.target as HTMLInputElement).value);
  }

  private _formKey = '';

  get form(): FormData | undefined { return this.msg?.licextForm; }
  get flowType(): string { return this.form?.flow_type ?? ''; }
  get submitted(): boolean { return !!this.msg?.licextFormSubmitted; }
  /** The wizard is interactive only on the latest agent message, before submit. */
  get locked(): boolean { return this.isPast || this.submitted; }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['msg']) return;
    const form = this.msg?.licextForm;
    if (!form) return;
    // Re-seed local state only when the form payload actually changes (a stable
    // key avoids clobbering in-progress edits on unrelated change-detection).
    const key = JSON.stringify(form);
    if (key === this._formKey) return;
    this._formKey = key;

    if (form.flow_type === 'device_step1') {
      const rows = (form.device_rows ?? []).map(r => ({
        serial: r.serial ?? '',
        invalid: !!r.invalid,
        error: r.error ?? '',
      }));
      // Always render the full set of MAX_ROWS input rows up front — the user
      // fills only as many as they need (blank rows are ignored on submit), so
      // there is no "add row" button to click repeatedly.
      this.deviceStep1Rows.set(this.padRows(rows, () => ({ serial: '', invalid: false, error: '' })));
    } else if (form.flow_type === 'device_step2') {
      // `selected_sku` is present only when the agent re-opens the form to
      // preserve a prior pick; guard it against a SKU that's no longer offered.
      this.deviceStep2Rows.set((form.device_step2_rows ?? []).map(r => {
        const skus = r.extension_skus ?? [];
        const preSku = r.selected_sku && skus.some(s => s.sku === r.selected_sku)
          ? r.selected_sku : null;
        return {
          serial: r.serial_number ?? '',
          accountId: r.account_id ?? '',
          skus,
          selectedSku: preSku,
          panelOpen: false,
        };
      }));
      this.reason.set(form.reason ?? '');
      this.accountName.set(form.account_name ?? '');
    } else if (form.flow_type === 'saas_step1') {
      const rows = (form.saas_rows ?? []).map(r => ({ containerKey: r.container_key ?? '' }));
      // Render all MAX_ROWS rows up front (see device_step1 note above).
      this.saasStep1Rows.set(this.padRows(rows, () => ({ containerKey: '' })));
    } else if (form.flow_type === 'saas_step2') {
      const options = (form.day_options ?? []).filter(
        (d): d is SaasDays => (DEFAULT_SAAS_DAY_OPTIONS as number[]).includes(d));
      this.saasDayOptions.set(options.length ? options : DEFAULT_SAAS_DAY_OPTIONS);
      // Restore any previously chosen products + days from `selected_days`.
      this.saasStep2Rows.set((form.saas_step2_rows ?? []).map(r => {
        const products = r.products ?? [];
        const selected: Record<number, SaasDays> = {};
        products.forEach((p, i) => {
          if (p.selected_days && this.saasDayOptions().includes(p.selected_days)) {
            selected[i] = p.selected_days;
          }
        });
        return {
          containerKey: r.container_key ?? '',
          accountId: r.account_id ?? '',
          products,
          selected,
          panelOpen: false,
        };
      }));
      this.reason.set(form.reason ?? '');
      this.accountName.set(form.account_name ?? '');
    }
  }

  /** Max input rows shown in either Step-1 form. All rows are rendered up front
   *  (no "add row" button); blank rows are filtered out on submit. */
  readonly MAX_ROWS = 5;

  /** Take the incoming rows, cap at MAX_ROWS, and pad with fresh blank rows so
   *  the form always shows exactly MAX_ROWS entries. */
  private padRows<T>(rows: T[], make: () => T): T[] {
    const out = rows.slice(0, this.MAX_ROWS);
    while (out.length < this.MAX_ROWS) out.push(make());
    return out;
  }

  // ── Device-or-SaaS chooser pills ─────────────────────────────────────────

  /** Click a "Device" / "SaaS" pill — sends the choice as a normal user
   *  message so the agent routes it through its existing named-flow logic. */
  chooseType(type: 'device' | 'saas'): void {
    if (this.locked) return;
    const msg = type === 'device'
      ? 'I would like to extend a Device license.'
      : 'I would like to extend a SaaS license.';
    this._emit(msg);
  }

  // ── Device step 1 (serial collection) ────────────────────────────────────

  updateDeviceSerial(i: number, value: string): void {
    // Editing a row clears its flagged-invalid state so the highlight goes away.
    this.deviceStep1Rows.update(rows =>
      rows.map((r, idx) => idx === i ? { serial: value, invalid: false, error: '' } : r));
  }

  /** True when two or more rows have the same (case-insensitive) serial number. */
  get duplicateSerials(): boolean {
    const serials = this.deviceStep1Rows().map(r => r.serial.trim().toLowerCase()).filter(Boolean);
    return new Set(serials).size !== serials.length;
  }

  get deviceStep1Valid(): boolean {
    const serials = this.deviceStep1Rows().map(r => r.serial.trim().toLowerCase()).filter(Boolean);
    // At least one serial, and all serials unique (mirrors saasStep1Valid).
    return serials.length > 0 && new Set(serials).size === serials.length;
  }

  submitDeviceStep1(): void {
    if (this.locked || !this.deviceStep1Valid) return;
    const filled = this.deviceStep1Rows().filter(r => r.serial.trim());
    const lines = filled.map((r, i) => `${i + 1}. Serial: ${r.serial.trim()}`);
    const seed = ['Please extend the following devices:', ...lines].join('\n');
    this._emit(seed);
  }

  // ── Device step 2 (per-device SKU selection) ─────────────────────────────

  get deviceStep2Valid(): boolean {
    const rows = this.deviceStep2Rows();
    return rows.length > 0 && rows.every(r => !!r.selectedSku)
      && !!this.reason().trim() && this.wordCount() <= this.REASON_MAX_WORDS
      && !!this.accountName().trim();
  }

  submitDeviceStep2(): void {
    if (this.locked || !this.deviceStep2Valid) return;
    const lines = this.deviceStep2Rows().map((r, i) => {
      // The selected SKU's `term` (e.g. "7d") is threaded into the seed so the
      // backend can resolve the extension length from it (falling back to the
      // SKU name only if the term is absent). Like a hidden field.
      const term = r.skus.find(s => s.sku === r.selectedSku)?.term ?? '';
      return `${i + 1}. Serial: ${r.serial}, Account ID: ${r.accountId}, SKU: ${r.selectedSku}, Term: ${term}`;
    });
    const seed = ['Please extend the following device items:', ...lines, `Account Name: ${this.accountName().trim()}`, `Reason: ${this.reason().trim()}`].join('\n');
    this._emit(seed);
  }

  /** Step-2 "edit serials" — go back to the step-1 form. Sends a plain message;
   *  the agent reopens the serial form (prompt Rule 11). */
  editSerials(): void {
    if (this.locked) return;
    this._emit('I would like to edit the device serial numbers.');
  }

  // ── SaaS step 1 ──────────────────────────────────────────────────────────

  updateSaasRow(i: number, value: string): void {
    this.saasStep1Rows.update(rows => rows.map((r, idx) => idx === i ? { containerKey: value } : r));
  }

  /** True when two or more rows have the same (case-insensitive) container key. */
  get duplicateContainerKeys(): boolean {
    const keys = this.saasStep1Rows().map(r => r.containerKey.trim().toLowerCase()).filter(Boolean);
    return new Set(keys).size !== keys.length;
  }

  get saasStep1Valid(): boolean {
    const keys = this.saasStep1Rows().map(r => r.containerKey.trim().toLowerCase()).filter(Boolean);
    // At least one key, and all keys unique (mirrors the legacy portal).
    return keys.length > 0 && new Set(keys).size === keys.length;
  }

  submitSaasStep1(): void {
    if (this.locked || !this.saasStep1Valid) return;
    const filled = this.saasStep1Rows().filter(r => r.containerKey.trim());
    const lines = filled.map((r, i) => `${i + 1}. Container Key: ${r.containerKey.trim()}`);
    const seed = ['Please extend the following SaaS licences:', ...lines].join('\n');
    this._emit(seed);
  }

  // ── SaaS step 2 ──────────────────────────────────────────────────────────

  togglePanel(rowIdx: number): void {
    if (this.locked) return;
    this.saasStep2Rows.update(rows => rows.map((r, idx) =>
      idx === rowIdx ? { ...r, panelOpen: !r.panelOpen } : r));
  }

  isProductSelected(rowIdx: number, prodIdx: number): boolean {
    return this.saasStep2Rows()[rowIdx]?.selected[prodIdx] !== undefined;
  }

  toggleProduct(rowIdx: number, prodIdx: number): void {
    if (this.locked) return;
    this.saasStep2Rows.update(rows => rows.map((r, idx) => {
      if (idx !== rowIdx) return r;
      const selected = { ...r.selected };
      if (selected[prodIdx] !== undefined) delete selected[prodIdx];
      else selected[prodIdx] = 7;
      return { ...r, selected };
    }));
  }

  setDays(rowIdx: number, prodIdx: number, days: SaasDays): void {
    if (this.locked) return;
    this.saasStep2Rows.update(rows => rows.map((r, idx) =>
      idx === rowIdx ? { ...r, selected: { ...r.selected, [prodIdx]: days } } : r));
  }

  /** Total selected products across all rows (max 5). */
  get selectedCount(): number {
    return this.saasStep2Rows().reduce((n, r) => n + Object.keys(r.selected).length, 0);
  }

  /** Number of products selected within one container row. */
  rowSelectedCount(row: SaasStep2UiRow): number {
    return Object.keys(row.selected).length;
  }

  /** Trigger label for a container's products dropdown. */
  saasProductsLabel(row: SaasStep2UiRow): string {
    const n = this.rowSelectedCount(row);
    return n ? `${n} product${n === 1 ? '' : 's'} selected` : 'Select products…';
  }

  get saasStep2Valid(): boolean {
    const n = this.selectedCount;
    return n > 0 && n <= 5 && !!this.reason().trim() && this.wordCount() <= this.REASON_MAX_WORDS
      && !!this.accountName().trim();
  }

  submitSaasStep2(): void {
    if (this.locked || !this.saasStep2Valid) return;
    const lines: string[] = [];
    let n = 0;
    for (const row of this.saasStep2Rows()) {
      for (const [pIdxStr, days] of Object.entries(row.selected)) {
        const p = row.products[Number(pIdxStr)];
        if (!p) continue;
        n += 1;
        // The Entitlement API gives us each product's activation key and its
        // CURRENT expiry; both are threaded through the seed so the backend can
        // compute the new expiry (current + days) and target the right product.
        // Activation Key is effectively a hidden field — carried in the seed,
        // never shown as an editable input.
        lines.push(
          `${n}. Container Key: ${row.containerKey}, Account ID: ${row.accountId}, ` +
          `Product: ${p.product_name ?? ''}, Activation Key: ${p.product_activation_key ?? ''}, ` +
          `Current Expiry: ${p.expiration_date ?? ''}, Days: ${days}`,
        );
      }
    }
    const seed = ['Please extend the following SaaS items:', ...lines, `Account Name: ${this.accountName().trim()}`, `Reason: ${this.reason().trim()}`].join('\n');
    this._emit(seed);
  }

  /** Step-2 "edit container keys" — go back to the step-1 form. Sends a plain
   *  message; the agent reopens the container-key form (prompt Rule 11). */
  editContainerKeys(): void {
    if (this.locked) return;
    this._emit('I would like to edit the container keys.');
  }

  // ── Approver selection ───────────────────────────────────────────────────

  /** The approver list for the select_approvers form (empty for other flows). */
  get approverList(): FormDataApprover[] {
    const f = this.form;
    return f && f.flow_type === 'select_approvers' ? (f.approvers ?? []) : [];
  }

  /** Emails to pre-select in the picker when the approver form is re-opened. */
  get approverPreselected(): string[] {
    const f = this.form;
    return f && f.flow_type === 'select_approvers' ? (f.selected_emails ?? []) : [];
  }

  /** The picker emits the chosen approvers; compose the seed the agent parses. */
  submitApprovers(approvers: FormDataApprover[]): void {
    if (this.locked || !approvers.length) return;
    const lines = approvers.map((a, i) =>
      `${i + 1}. Name: ${a.name ?? ''}, Email: ${a.email ?? ''}, Region: ${a.region ?? ''}`);
    const seed = ['Selected approvers:', ...lines].join('\n');
    this._emit(seed);
  }

  // ── Dispatch ─────────────────────────────────────────────────────────────

  private _emit(seed: string): void {
    this.action.emit({ type: 'submit', payload: seed, msgIdx: this.msgIdx });
  }

  trackByIndex(i: number): number { return i; }
}
